import { MenuItem } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../redux/ReduxHooks';
import { ApplicationActions } from '../../../utils/constants';
import { persistDashboardLayoutToProject, updateWidgetInDashboardLayout } from '../persistDashboardWidgetContent';
import type { DashboardWidgetContent } from '../dashboardTypes';
import { stableJsonSignature } from '../../../utils/stableJsonSignature';

export type SimpleWidgetStyle = 'default' | 'raw' | 'number';

function toSimpleStyle(style: string): SimpleWidgetStyle {
  if (style === 'raw') return 'raw';
  if (style === 'number') return 'number';
  return 'default';
}

/**
 * Manages the display style for widgets that support switching between their
 * native view ('default'), Value Table ('raw'), and Live Number ('number').
 *
 * Returns `styleMenuItems` — the two shared "Value Table Reading" and
 * "Live Number Reading" menu items, pre-bound to the style setter. Each widget
 * only needs to prepend its own default-view label.
 *
 * ## Persisted `style` in dashboard layout
 *
 * | Value | Meaning |
 * |-------|---------|
 * | `'raw'` / `'number'` | Value table / live number (same keys as line graph widgets). |
 * | `'default'` | Native view for gauge, orientation, or position (saved when the user picks that widget's default menu item). |
 * | `'line'` (and other graph styles) | Legacy default from `createDashboardWidgetContent`; mapped to the native view by `toSimpleStyle`. |
 *
 * Native menu actions persist `'default'` rather than `'line'` so the saved layout
 * means "this widget's built-in view", not "line chart". Existing projects may still
 * have `style: 'line'` for speed/position/orientation widgets; both render the native view.
 */
export function usePersistedWidgetStyle(id: string, styleProp: string) {
  const dispatch = useDispatch();
  const dashboardLayout = useAppSelector((state) => state.project.loadedProject?.dashboardLayout);
  const dashboardLayoutRef = useRef(dashboardLayout);
  dashboardLayoutRef.current = dashboardLayout;
  const dashboardLayoutSignature = useMemo(() => stableJsonSignature(dashboardLayout ?? []), [dashboardLayout]);
  const dashboardViewMode = useAppSelector((state) => state.application.dashboardViewMode);
  const isHistoricMode = dashboardViewMode === ApplicationActions.DashboardViewMode.HISTORIC;

  const [widgetStyle, setWidgetStyle] = useState<SimpleWidgetStyle>(() => toSimpleStyle(styleProp));

  // Keep local state in sync with the persisted prop when the style prop changes
  // or the user switches view modes (mirrors the same pattern in GraphWidget).
  useEffect(() => {
    setWidgetStyle(toSimpleStyle(styleProp));
  }, [styleProp, dashboardViewMode]);

  const handleSetStyle = useCallback(
    (style: SimpleWidgetStyle) => {
      setWidgetStyle(style);
      if (isHistoricMode) return;
      const layout = dashboardLayoutRef.current;
      if (!layout) return;

      const mergeContent = (content: DashboardWidgetContent): DashboardWidgetContent => ({ ...content, style });
      const nextLayout = updateWidgetInDashboardLayout(layout, id, mergeContent);
      if (!nextLayout) return;

      persistDashboardLayoutToProject(dispatch, nextLayout);
    },
    // dashboardLayoutSignature invalidates when layout content changes; layout is read from dashboardLayoutRef
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dashboardLayoutSignature, dispatch, id, isHistoricMode]
  );

  const styleMenuItems = useMemo(
    () => (
      <>
        <MenuItem onClick={() => handleSetStyle('raw')}>
          <Trans i18nKey="lineGraphWidget.menuItem.value-table">Value Table Reading</Trans>
        </MenuItem>
        <MenuItem onClick={() => handleSetStyle('number')}>
          <Trans i18nKey="lineGraphWidget.menuItem.live-reading">Live Number Reading</Trans>
        </MenuItem>
      </>
    ),
    [handleSetStyle]
  );

  return { widgetStyle, handleSetStyle, styleMenuItems };
}
